import React, { useState, useRef, useEffect, useMemo, ChangeEvent, KeyboardEvent } from 'react';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/_shadcn-ui/popover';
import Avatar from '@/components/ui/avatar';
import { AutoResizeTextarea } from '@/components/ui/_refactor/form/textareas';
import { handleTextareaAutoResize } from '@/features/ticketing/tickets/utils/ticket.utils';
import { TTicketMember } from '@/features/ticketing/tickets/types/ticket.type';
import { cn } from '@/utils/cn';

// Utility to escape regex special chars
const escapeRegExp = (string: string) => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

// Internal mention structure for processing
interface Mention {
  id: string;
  name: string;
  avatar?: string;
  description?: string;
}

// Props type for the MentionTextarea component
interface MentionTextareaProps {
  value: string;
  onChange: (value: string) => void;
  onChangeFormatted?: (formattedValue: string) => void;
  mentions: TTicketMember[];
  placeholder?: string;
  trigger?: string;
  className?: string;
  maxSuggestions?: number;
  hasError?: boolean;
}

// Main MentionTextarea Component
const TextareaMention: React.FC<MentionTextareaProps> = ({
  value,
  onChange,
  onChangeFormatted,
  mentions = [],
  placeholder = 'Type @ to mention someone...',
  trigger = '@',
  className = '',
  maxSuggestions,
  hasError = false,
}) => {
  // Map TTicketMember[] to internal Mention[] format
  const mappedMentions = useMemo<Mention[]>(() => {
    return mentions.map((member) => ({
      id: member.userId,
      name: member.fullname,
      avatar: member.profileImage,
      description: undefined, // TTicketMember doesn't have description
    }));
  }, [mentions]);

  const [displayValue, setDisplayValue] = useState<string>(value); // What user sees
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const [suggestions, setSuggestions] = useState<Mention[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [cursorPosition, setCursorPosition] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const suggestionsRef = useRef<HTMLDivElement | null>(null);

  // keep previous value to detect deletion
  const prevDisplayRef = useRef<string>(value);

  // Parse formatted text (from server) to display text
  const _parseFormattedToDisplay = (formatted: string): string => {
    return formatted.replace(/@([^|]+)\|([^|]+)/g, '@$1');
  };

  // Parse display text to formatted text (for server)
  // Example:
  //   display:  "@John Doe hello @Jane"
  //   formatted:"@John Doe|userId1 hello @Jane|userId2"
  //
  // IMPLEMENTATION NOTES:
  // - We FIRST find all mention ranges in the *original* display text (using longest-name-first logic)
  // - Then we build the formatted string in a single pass, replacing each detected mention
  // - This avoids repeatedly running regex replaces over an already-formatted string, which caused
  //   overlapping names (e.g. 'Arief' vs 'Arief Rachman Hakim') to be re‑matched and corrupted
  const parseDisplayToFormatted = (display: string): string => {
    if (!display) return '';

    // Find all potential mention ranges in the display text
    const ranges = findMentionRanges(display);

    if (!ranges.length) {
      return display;
    }

    // Sort ranges by start index so we can walk from left to right
    ranges.sort((a, b) => a.start - b.start);

    let result = '';
    let cursor = 0;
    let lastEnd = 0;

    for (const range of ranges) {
      // Skip ranges that overlap with an already-taken range.
      // Because findMentionRanges prefers longer names first, this keeps the longest match.
      if (range.start < lastEnd) continue;

      // Add plain text between previous cursor and this mention
      if (cursor < range.start) {
        result += display.slice(cursor, range.start);
      }

      // Add formatted mention
      result += `@${range.mention.name}|${range.mention.id}`;

      cursor = range.end;
      lastEnd = range.end;
    }

    // Add any remaining text after the last mention
    if (cursor < display.length) {
      result += display.slice(cursor);
    }

    return result;
  };

  // Helper: find all mention ranges (in a given text) as {start, endExclusive, mention}
  const findMentionRanges = (text: string) => {
    const ranges: { start: number; end: number; mention: Mention }[] = [];
    const sorted = [...mappedMentions].sort((a, b) => b.name.length - a.name.length);
    sorted.forEach((m) => {
      const mentionText = `${trigger}${m.name}`;
      const regex = new RegExp(escapeRegExp(mentionText), 'g');
      let match;
      while ((match = regex.exec(text)) !== null) {
        ranges.push({
          start: match.index,
          end: match.index + mentionText.length, // exclusive
          mention: m,
        });
        if (regex.lastIndex === match.index) regex.lastIndex++;
      }
    });
    return ranges;
  };

  // Filter suggestions based on search query
  useEffect(() => {
    if (searchQuery) {
      const filtered = mappedMentions
        .filter((mention) => mention.name.toLowerCase().includes(searchQuery.toLowerCase()))
        .slice(0, maxSuggestions);
      setSuggestions(filtered);
      setSelectedIndex(0);
    } else {
      setSuggestions(mappedMentions.slice(0, maxSuggestions));
    }
  }, [searchQuery, mappedMentions, maxSuggestions]);

  // Handle text changes
  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const newDisplayValueRaw = e.target.value;
    const position = e.target.selectionStart;

    // detect deletion compared to previous value
    const prev = prevDisplayRef.current;
    const isDeletion = newDisplayValueRaw.length < prev.length;

    let newDisplayValue = newDisplayValueRaw;
    let newCursorPos = position;

    if (isDeletion) {
      const ranges = findMentionRanges(prev);

      const deletionIndexInPrev = position;

      const containing = ranges.find(
        (r) =>
          (deletionIndexInPrev > r.start && deletionIndexInPrev <= r.end) ||
          (deletionIndexInPrev === r.start && prev[r.start] === trigger)
      );
      if (containing) {
        const before = prev.slice(0, containing.start);
        const after = prev.slice(containing.end);

        let adjustedAfter = after;
        if (adjustedAfter.startsWith(' ')) {
          adjustedAfter = adjustedAfter.slice(1);
        }

        newDisplayValue = before + adjustedAfter;
        newCursorPos = before.length;

        setDisplayValue(newDisplayValue);
        setCursorPosition(newCursorPos);
        if (onChange) onChange(newDisplayValue);
        if (onChangeFormatted) {
          const formatted = parseDisplayToFormatted(newDisplayValue);
          onChangeFormatted(formatted);
        }

        prevDisplayRef.current = newDisplayValue;
        setTimeout(() => {
          const ta = textareaRef.current;
          if (ta) {
            ta.setSelectionRange(newCursorPos, newCursorPos);
            ta.focus();
          }
        }, 0);

        return;
      }
    }

    setDisplayValue(newDisplayValue);
    setCursorPosition(position);

    if (onChange) onChange(newDisplayValue);

    if (onChangeFormatted) {
      const formatted = parseDisplayToFormatted(newDisplayValue);
      onChangeFormatted(formatted);
    }

    const textBeforeCursor = newDisplayValue.slice(0, position);
    const lastTriggerIndex = textBeforeCursor.lastIndexOf(trigger);

    if (lastTriggerIndex !== -1) {
      const textAfterTrigger = textBeforeCursor.slice(lastTriggerIndex + 1);

      if (!textAfterTrigger.includes(' ') && !textAfterTrigger.includes('\n')) {
        setSearchQuery(textAfterTrigger);
        setShowSuggestions(true);
      } else {
        setShowSuggestions(false);
      }
    } else {
      setShowSuggestions(false);
    }

    prevDisplayRef.current = newDisplayValue;
  };

  // Insert mention at cursor position
  const insertMention = (mention: Mention) => {
    const textarea = textareaRef.current;
    const textBeforeCursor = displayValue.slice(0, cursorPosition);
    const textAfterCursor = displayValue.slice(cursorPosition);

    const lastTriggerIndex = textBeforeCursor.lastIndexOf(trigger);
    const beforeTrigger = displayValue.slice(0, lastTriggerIndex);
    const mentionText = `${trigger}${mention.name}`;
    const newDisplayValue = beforeTrigger + mentionText + ' ' + textAfterCursor;

    setDisplayValue(newDisplayValue);
    setCursorPosition(beforeTrigger.length + mentionText.length + 1);
    if (onChange) onChange(newDisplayValue);

    if (onChangeFormatted) {
      const formatted = parseDisplayToFormatted(newDisplayValue);
      onChangeFormatted(formatted);
    }

    setShowSuggestions(false);
    setSearchQuery('');

    prevDisplayRef.current = newDisplayValue;

    setTimeout(() => {
      const newPosition = beforeTrigger.length + mentionText.length + 1;
      textarea?.setSelectionRange(newPosition, newPosition);
      textarea?.focus();
    }, 0);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (!showSuggestions) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        break;
      case 'Enter':
        if (suggestions.length > 0) {
          e.preventDefault();
          insertMention(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowSuggestions(false);
        break;
    }
  };

  // Scroll selected suggestion into view
  useEffect(() => {
    if (suggestionsRef.current && showSuggestions) {
      const selectedElement = suggestionsRef.current.children[selectedIndex];
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex, showSuggestions]);

  // keep prevDisplayRef in sync when value prop changes externally
  useEffect(() => {
    setDisplayValue(value);
    prevDisplayRef.current = value;
  }, [value]);

  return (
    <div className="relative w-full">
      <Popover open={showSuggestions && suggestions.length > 0}>
        <PopoverAnchor asChild>
          <div
            className={cn(
              'relative border-[1.5px] rounded-[8px] overflow-visible w-full',
              hasError
                ? 'border-[#CC0202] focus-within:border-[#CC0202]'
                : 'border-border focus-within:border-[#0C8DBF]'
            )}
          >
            <AutoResizeTextarea
              minHeight={36}
              value={displayValue}
              ref={textareaRef}
              className={cn(
                'overflow-y-auto w-full bg-white text-[12px] min-h-[36px] max-h-[120px] resize-none focus-visible:outline-none focus-visible:border-[#0C8DBF] h-auto focus-visible:ring-0 focus-visible:ring-transparent focus-visible:outline-0 border-none',
                className
              )}
              placeholder={placeholder}
              rows={1}
              onInput={(e) => handleTextareaAutoResize(e, 120)}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
            />
          </div>
        </PopoverAnchor>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0 max-h-64 overflow-y-auto"
          ref={suggestionsRef}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onInteractOutside={(e) => {
            // Prevent closing when clicking on the textarea
            if (e.target === textareaRef.current || textareaRef.current?.contains(e.target as Node)) {
              e.preventDefault();
            }
          }}
        >
          {suggestions.map((mention, index) => (
            <button
              key={mention.id}
              data-mention-index={index}
              onMouseDown={(e) => {
                // Prevent focus from moving to button
                e.preventDefault();
                insertMention(mention);
              }}
              className={`w-full px-4 py-3 flex items-center gap-3 text-left transition-colors ${
                index === selectedIndex ? 'bg-accent text-accent-foreground' : 'hover:bg-muted text-foreground'
              }`}
            >
              <Avatar
                imgSrc={mention.avatar || ''}
                username={mention.name}
                className="w-[20px] h-[20px] flex-shrink-0"
              />
              <div className="flex flex-col">
                <span className="truncate text-[#25151E] text-[14px] font-[500]">{mention.name}</span>
                {mention.description && (
                  <span
                    className={`text-[12px] ${index === selectedIndex ? 'text-accent-foreground/70' : 'text-muted-foreground'}`}
                  >
                    {mention.description}
                  </span>
                )}
              </div>
            </button>
          ))}
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default TextareaMention;
